// packages
import { ascending, descending, max } from 'd3-array';
import { pointer, select } from 'd3-selection';
import { scaleLog, scaleThreshold } from 'd3-scale';
import { line as createLine } from 'd3-shape';
import { intcomma } from 'journalize';

// utils
import { closestPoint } from '../../utils/closest-point';
import { createBase } from '../../utils/d3-base';
import { last } from '../../utils/last';
import { loadJsonScript } from '../../utils/load-json-script';
import { occlusion } from '../../utils/occlusion';

import Legend from '../Legend.svelte';

// we never go below this
const cutoff = 10;

// make this easier to change
const nameKey = 'county';
const valuesKey = 'curve';
const labelSortKey = 'doubling_time_cases';
const xAttr = 'days_since_10';
const yAttr = 'reported_and_probable_cases';
const colorKey = 'doubling_time_cases';

// how we de-highlight
const fadeColor = '#dddddd';

// what ticks we show on the x axis
const xAxisTickValues = [10, 100, 250, 500];

// what ticks should get labeled on the y axis
const yAxisTickLabels = [
  10,
  20,
  50,
  100,
  200,
  500,
  1000,
  2000,
  5000,
  10000,
  20000,
  // 40000,
  50000,
  // 60000,
  100000,
  200000,
  500000,
];

// these will always "win" regardless of their doubling rates (unless they overlap)
const importantLabels = [
  'Los Angeles',
  'Santa Clara',
  'Sacramento',
  'San Diego',
];

// in lieu of doing crazy math to "flip" labels, we hard code them here
let labelsToInvert = [
  'Los Angeles',
  'Santa Clara',
  'Contra Costa',
  'San Francisco',
  'San Diego',
];

// extra ones that only get flipped on mobile (<544px)
let labelsToInvertMobile = [
  'San Francisco',
  'Contra Costa',
  'Sacramento',
  'San Diego',
  'Marin',
  'Orange',
  'Alameda',
];

const mobileWidth = 400;

const sortGroups = (a, b) => {
  const aValue = a[labelSortKey] === -1 ? Infinity : a[labelSortKey];
  const bValue = b[labelSortKey] === -1 ? Infinity : b[labelSortKey];
  return descending(aValue, bValue);
};

// the raw data on window
const data = loadJsonScript('counties-timeseries-data')
  .filter((d) => Boolean(d[valuesKey].length))
  .sort(sortGroups);

// our max on the x domain is the county with the most days, find it
const maxDays = max(data, (d) => d[valuesKey].length);

// our max on the y domain is the highest number of cases, find it
const maxValue = max([
  max(data, ({ reported_and_probable_cases }) => reported_and_probable_cases),
  last(yAxisTickLabels),
]);

// built-in static lines
const staticDoubles = [
  [1, 'day', 10, 30000],
  [3, '3 days', 32, 20000],
  [7, 'week', 70, 8000],
  [30, 'month', 70, 40],
];

const slopes = staticDoubles.map(([rate, label, x, y]) => {
  const final = applyDoublingRate(cutoff, maxDays, rate);

  const data = [
    {
      days_since_10: 0,
      reported_and_probable_cases: cutoff,
    },
    {
      days_since_10: maxDays,
      reported_and_probable_cases: final,
    },
  ];

  return { data, x, y, rate, label };
});

// our container of choice
const container = select('#county-compare-chart');

const palette0 = [
  '#ccebc5',
  '#B6DCAE',
  '#74CEE2',
  '#50ADD3',
  '#1789BE',
  '#08589e',
];
const palette = palette0.reverse();

const finalColor = palette[palette.length - 1];

const color = scaleThreshold().domain([10, 30, 40, 60, 80, 120]).range(palette);

const getColor = (val) => {
  if (val === -1) {
    return finalColor;
  }
  return color(val);
};

new Legend({
  target: document.querySelector('#county-compare-legend'),
  props: {
    color: color,
    height: 50,
    title: 'Current doubling time',
    tickLabel: ' days',
  },
});

export function render() {
  // first empty the container on render every time
  container.html('');

  // create our base pieces
  const { g, x, y, width, height, xAxis, yAxis, drawAxis } = createBase({
    container,
    y: scaleLog(),
    margin: { bottom: 35, left: 3, right: 50 },
  });

  const ltMobileWidth = width < mobileWidth;

  if (ltMobileWidth) {
    labelsToInvert = labelsToInvert.concat(labelsToInvertMobile);
  }

  // set our domain for the x axis (range is handled in createBase)
  x.domain([0, maxDays]).clamp();

  // set our domain and range for the y axis
  y.domain([cutoff, maxValue]);

  // tell the x axis we only want these ticks
  xAxis.tickValues(xAxisTickValues);

  // only allow certain ticks to get labels
  yAxis
    .tickFormat((d) => (yAxisTickLabels.includes(d) ? intcomma(d) : null))
    .tickSize(-width);

  // grab the selector post-render of the axis
  const { xAxisSel, yAxisSel } = drawAxis();

  yAxisSel.call((g) =>
    g
      .selectAll('.tick text')
      .attr('text-anchor', 'start')
      .attr('x', 0)
      .attr('dy', -4)
  );

  // we're gonna muck with the first text label
  const firstXText = xAxisSel.select('text');

  // clean it out first
  firstXText.text('');

  // we tspan'in
  firstXText
    .attr('text-anchor', 'start')
    .append('tspan')
    .text((d) => `${d} days`)
    .attr('x', -3);

  // add our qualifier
  firstXText
    .append('tspan')
    .text('since 10th case')
    .attr('x', -3)
    .attr('dy', 15);

  //  // create our line generator
  //  const drawSlope = createLine()
  //    .x((d) => x(d[xAttr]))
  //    .y((d) => y(d[yAttr]));

  //  const slopesGroup = g.append('g').attr('class', 'slopes');

  //  const slopeGroup = slopesGroup
  //    .selectAll('g.slope')
  //    .data(slopes)
  //    .join('g')
  //    .attr('class', 'slope');

  //  slopeGroup.append('path').attr('d', (d) => drawSlope(d.data));

  //  const slopeText = slopeGroup
  //    .append('text')
  //    .classed('label--halo', true)
  //    .attr('text-anchor', 'start')
  //    .attr(
  //      'transform',
  //      (d) => `translate(${x(d.x) - (ltMobileWidth ? 20 : 0)},${y(d.y)})`
  //    );

  //  slopeText
  //    .append('tspan')
  //    .attr('x', 0)
  //    .attr('dy', 0)
  //    .text((d) => {
  //      const first = d.rate === 1;
  //      const last = d.rate === 7;

  //      if (first) {
  //        return 'Doubling';
  //      } else if (last) {
  //        return 'Every week';
  //      } else {
  //        return 'Every';
  //      }
  //    });

  //  slopeText
  //    .append('tspan')
  //    .attr('x', 0)
  //    .attr('dy', 13)
  //    .text((d) => {
  //      const first = d.rate === 1;
  //      const last = d.rate === 7;

  //      if (first) {
  //        return 'every day';
  //      } else if (last) {
  //        return '';
  //      } else {
  //        return d.label;
  //      }
  //    });

  //  slopeText.clone(true).classed('label--halo', false);

  // prep our big county grouping element
  const groups = g.append('g').attr('class', 'groups');

  // we're gonna group each county's stuff together so the cascade works
  const group = groups
    .selectAll('g.group')
    .data(data)
    .join('g')
    .attr('class', 'group')
    .attr('data-fips', (d) => d.fips)
    .attr('data-county', (d) => d.county)
    .attr('fill', (d) => getColor(d[colorKey]))
    .attr('stroke', (d) => getColor(d[colorKey]));

  const drawLine = createLine()
    .x((d, i) => x(i))
    .y((d) => y(d));

  // draw the halo lines for each county...
  const line = group
    .append('path')
    .attr('class', 'line line--halo')
    .attr('d', (d) => drawLine(d[valuesKey]));

  // ...then clone 'em and de-halo for the top layer
  line.clone(true).classed('line--halo', false);

  // draw our guide dots on the line
  const dot = group
    .selectAll('circle.dot')
    .data((d) => d[valuesKey])
    .join('circle')
    .attr('class', `dot${ltMobileWidth ? ' display-none' : ''}`)
    .attr('r', '2')
    .attr('cx', (_, i) => x(i))
    .attr('cy', (d) => y(d));

  // we create a separate group for our labels because we always want them on top
  const labels = g.append('g').attr('class', 'labels');

  // place and space our label halos...
  const label = labels
    .selectAll('text.label.label--halo')
    .data(data)
    .join('text')
    .attr('class', 'label label--halo')
    .attr('transform', (d) => {
      const final = last(d[valuesKey]);
      const shouldInvert = labelsToInvert.includes(d[nameKey]);

      return `translate(${
        x(d[valuesKey].length - 1) + (shouldInvert ? -6 : 6)
      },${y(final) + (shouldInvert ? -3 : 4)})`;
    })
    .attr('text-anchor', (d) =>
      labelsToInvert.includes(d[nameKey]) ? 'end' : 'start'
    );

  label
    .selectAll('tspan')
    .data((d) =>
      d[colorKey] === -1
        ? [
            d[nameKey],
            `${intcomma(d.reported_and_probable_cases)} cases`,
            `no new cases`,
            `in last 7 days`,
          ]
        : [
            d[nameKey],
            `${intcomma(d.reported_and_probable_cases)} cases`,
            `now doubling`,
            `every ${d.doubling_time_cases.toFixed(1)} days`,
          ]
    )
    .join('tspan')
    .attr('class', (_, i) =>
      i === 0 ? 'tspan-county-name' : 'tspan-county-data'
    )
    .attr('x', 0)
    .attr('dy', (_, i) => {
      if (i === 0) {
        return 0;
      }

      if (i === 1) {
        return 14;
      }

      return 12;
    })
    .text((d) => d);

  const nameMapping = new Map();

  label
    .sort((a, b) => {
      const aValue = a[labelSortKey];

      return ascending(
        importantLabels.includes(a[nameKey]) ? aValue - maxValue : aValue,
        b[labelSortKey]
      );
    })
    .call(occlusion, 'display-none', 'county', 4, nameMapping);

  // ...then clone and de-halo them
  label.clone(true).classed('label--halo', false);

  const text = g.selectAll('.label');

  const overlay = g
    .append('rect')
    .attr('class', 'overlay')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height);

  overlay.on('mouseenter', moved).on('mousemove', moved).on('mouseleave', left);

  function moved(event) {
    const coords = pointer(event);

    let closestDistance = Infinity;
    let closestGroup = null;

    group.each((_, i, nodes) => {
      const node = nodes[i];
      const l = node.querySelector('.line');

      const { distance } = closestPoint(l, coords);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestGroup = node;
      }
    });

    if (closestDistance < 80) {
      g.classed('has-interaction', true);

      const sel = select(closestGroup).datum();
      const name = sel[nameKey];

      // highlight and raise the active one
      group
        .classed('is-highlighted', (_, i, nodes) => closestGroup === nodes[i])
        .filter('.is-highlighted')
        .raise();

      group
        .attr('stroke', (d, i, nodes) =>
          closestGroup === nodes[i] ? getColor(d[colorKey]) : fadeColor
        )
        .filter((_, i, nodes) => closestGroup === nodes[i])
        .raise();

      dot.attr('r', '3');

      text.classed('display-none', (d) => d[nameKey] !== name);
    } else {
      left();
    }
  }

  function left() {
    // disable interaction
    g.classed('has-interaction', false);

    group
      .classed('is-highlighted', false) // disable any active lines
      .attr('stroke', (d) => getColor(d[colorKey])) // reapply our color
      .sort(sortGroups); // reorder

    dot.attr('r', '2');

    text.classed('display-none', (d) => nameMapping.get(d[nameKey]));
  }
}

function applyDoublingRate(value, time, doublingRate) {
  return value * Math.pow(2, time / doublingRate);
}
